<template>
  <template v-if="resolvedTemplate">
    <!--
      Blocks with a configured anchor id are wrapped in a `<section :id>` so that
      in-page links (e.g. Context Navigation Tabs `#id`) can scroll to them.
      `scroll-mt-[48px] lg:scroll-mt-[64px]` keeps the section from hiding under the sticky Context Navigation Tabs bar.
      Rendered on the live page and in admin previews so anchors can be tested
      from the page configurator.
    -->
    <section v-if="anchorId" :id="anchorId" class="scroll-mt-[48px] lg:scroll-mt-[64px]">
      <component :is="resolvedTemplate" v-bind="templateProps" />
    </section>
    <component :is="resolvedTemplate" v-else v-bind="templateProps" />
  </template>
  <div v-else class="text-gray-500 text-sm">
    Nie znaleziono szablonu bloku „{{ block?.template?.vue_component }}”
  </div>
</template>

<script>
// Template registry: auto-discover every *.vue under @/Templates/ at build time.
// File basename without ".vue" becomes the lookup key (matches
// `Template#vue_component`). Adding a new template requires zero edits here -
// just drop the new .vue + .template.json file under @/Templates/ and run
// `bin/rails templates:sync` (already hooked into db:seed).
const templateModules = import.meta.glob('@/Templates/**/*.vue', { eager: true });

const templateMap = Object.fromEntries(
  Object.entries(templateModules).map(([path, mod]) => {
    const basename = path.split('/').pop().replace(/\.vue$/, '');
    return [basename, mod.default];
  }),
);

export default {
  name: 'DynamicBlock',
  props: {
    block: {
      type: Object,
      required: true,
    },
  },
  computed: {
    resolvedTemplate() {
      if (!this.block?.template?.vue_component) return null;
      return templateMap[this.block.template.vue_component] || null;
    },
    // In-page anchor for this block. Stored under the reserved `anchorId` key in
    // block.data (set per-block in the admin block editor, non-localized).
    anchorId() {
      if (!this.block) return null;
      const raw =
        this.block.anchor_id
        ?? this.block.anchorId
        ?? this.block.data?.anchorId
        ?? this.block.data?.anchor_id;
      if (raw == null) return null;
      const trimmed = String(raw).trim();
      return trimmed || null;
    },
    templateProps() {
      if (!this.block) return {};

      // `anchorId` is a structural concern handled by the wrapper above; strip it
      // so it never falls through onto the template's root element as an attr.
      const blockData = { ...(this.block.data || {}) };
      delete blockData.anchorId;
      delete blockData.anchor_id;

      // Defaults first (block.site_context), then block.data overrides.
      // System blocks have site_context=null on the Block row itself, and the
      // DataSourceResolver-supplied `data.site_context` is what we actually
      // want to expose to the template (it's the URL's site context).
      const props = {
        site_context: this.block.site_context,
        preview_mode: Boolean(this.block.preview_mode),
        ...blockData,
      };

      if (this.block.images && typeof this.block.images === 'object') {
        const imageFields =
          this.block.template?.schema?.fields?.filter((f) => f.type === 'image') || [];

        imageFields.forEach((field) => {
          const imageValue = this.block.images[field.name];
          if (imageValue) {
            props[field.name] =
              typeof imageValue === 'string' ? imageValue : imageValue.url || imageValue;
          }
        });
      }

      return props;
    },
  },
};
</script>
